import { FC, useEffect, useMemo, useState } from "react";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import MainTitle from "../../components/mainTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import {
  useClassByIdHook,
  useCourseByIdHook,
  useSectionsByClassHook,
  useCreateSectionHook,
  useUpdateSectionHook,
  useDeleteSectionHook,
  useClassSubjectsHook,
  useSubjectsHook,
  useAssignClassSubjectHook,
  useUnassignClassSubjectHook,
  useUpdateClassSubjectHook,
  useSectionTeachersHook,
  useAssignSectionTeacherHook,
  useUnassignSectionTeacherHook,
  useInternalUsersHook,
} from "../../../hooks/useAcademicHook";
import { logger } from "../../../utils/common";
import {
  TSection,
  TClassSubject,
} from "../../../apis/academic/academic.interfaces";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import AcademicHierarchySectionPicker from "../../components/academicHierarchy/AcademicHierarchySectionPicker";
import ClassFeeStructurePanel from "./classFeeStructurePanel.view";

const AcademicsClassHubView: FC = () => {
  const { classId = "", sectionId } = useParams<{
    classId: string;
    sectionId?: string;
  }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selectedInstitute } = useInstituteContext();

  const { data: classRow, isLoading: classLoading, error: classError } =
    useClassByIdHook(classId);
  const { data: course } = useCourseByIdHook(classRow?.courseId);
  const { data: sectionList, isLoading: sectionsLoading } =
    useSectionsByClassHook(classId);
  const { data: classSubjects, isLoading: csLoading } =
    useClassSubjectsHook(classId);
  const { data: catalogSubjects } = useSubjectsHook();

  const createSection = useCreateSectionHook();
  const updateSection = useUpdateSectionHook();
  const deleteSection = useDeleteSectionHook();
  const assignSubject = useAssignClassSubjectHook();
  const unassignSubject = useUnassignClassSubjectHook();
  const updateClassSubject = useUpdateClassSubjectHook();
  const assignTeacher = useAssignSectionTeacherHook();
  const unassignTeacher = useUnassignSectionTeacherHook();
  const { data: internalUsers = [] } = useInternalUsersHook();

  const { data: sectionTeachers, isLoading: teachersLoading } =
    useSectionTeachersHook(sectionId);

  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<TSection | null>(null);
  const [assignSubjectOpen, setAssignSubjectOpen] = useState(false);
  const [teacherFormOpen, setTeacherFormOpen] = useState(false);
  const [editTeacherRow, setEditTeacherRow] = useState<{
    subject: TClassSubject;
  } | null>(null);

  const sectionForm = useForm({
    defaultValues: { name: "", description: "" },
  });
  const assignForm = useForm({
    defaultValues: { subjectId: "", subjectTeacherId: "" },
  });
  const teacherAssignForm = useForm({
    defaultValues: { userId: "", roleLabel: "" },
  });
  const editTeacherForm = useForm({
    defaultValues: { subjectTeacherId: "" },
  });

  useEffect(() => {
    if (!sectionDialogOpen) return;
    if (editingSection) {
      sectionForm.reset({
        name: editingSection.name,
        description: editingSection.description ?? "",
      });
    } else {
      sectionForm.reset({ name: "", description: "" });
    }
  }, [sectionDialogOpen, editingSection, sectionForm]);

  useEffect(() => {
    if (!editTeacherRow) return;
    editTeacherForm.reset({
      subjectTeacherId: editTeacherRow.subject.subjectTeacherId ?? "",
    });
  }, [editTeacherRow, editTeacherForm]);

  const assignedSubjectIds = useMemo(
    () => new Set((classSubjects ?? []).map((r) => r.subjectId)),
    [classSubjects],
  );

  const availableSubjects = useMemo(
    () =>
      (catalogSubjects ?? []).filter((s) => !assignedSubjectIds.has(s.id)),
    [catalogSubjects, assignedSubjectIds],
  );

  useEffect(() => {
    if (!assignSubjectOpen) return;
    assignForm.reset({
      subjectId: availableSubjects[0]?.id ?? "",
      subjectTeacherId: "",
    });
  }, [assignSubjectOpen, availableSubjects, assignForm]);

  const hubBase = `${pages.academics.path}/classes/${classId}`;
  const feeStructurePath = `${hubBase}/fee-structure`;
  const isFeeStructureRoute = pathname.includes("/fee-structure");
  const sectionsTabPath = sectionId
    ? `${hubBase}/sections/${sectionId}`
    : hubBase;

  const openTeachersForSection = (sid: string) => {
    navigate(`${hubBase}/sections/${sid}`);
  };

  const saveSection = sectionForm.handleSubmit(async (vals) => {
    if (!classId) return;
    try {
      if (editingSection) {
        await updateSection.mutateAsync({
          id: editingSection.id,
          classId,
          request: {
            name: vals.name,
            description: vals.description || undefined,
          },
        });
        logger.show_success("Section updated.");
      } else {
        await createSection.mutateAsync({
          classId,
          request: { name: vals.name, description: vals.description || undefined },
        });
        logger.show_success("Section created.");
      }
      setSectionDialogOpen(false);
      setEditingSection(null);
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not save section.",
      );
    }
  });

  const handleDeleteSection = async (sec: TSection) => {
    if (!window.confirm(`Delete section "${sec.name}"?`)) return;
    try {
      await deleteSection.mutateAsync({ id: sec.id, classId });
      logger.show_success("Section deleted.");
      if (sectionId === sec.id) {
        navigate(hubBase);
      }
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not delete section.",
      );
    }
  };

  const saveAssignSubject = assignForm.handleSubmit(async (vals) => {
    if (!classId || !vals.subjectId) return;
    try {
      await assignSubject.mutateAsync({
        classId,
        request: {
          subjectId: vals.subjectId,
          subjectTeacherId: vals.subjectTeacherId.trim() || undefined,
        },
      });
      logger.show_success("Subject assigned to class.");
      setAssignSubjectOpen(false);
      assignForm.reset({ subjectId: "", subjectTeacherId: "" });
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not assign subject.",
      );
    }
  });

  const saveTeacherAssign = teacherAssignForm.handleSubmit(async (vals) => {
    if (!sectionId || !vals.userId.trim()) return;
    try {
      await assignTeacher.mutateAsync({
        sectionId,
        userId: vals.userId.trim(),
        roleLabel: vals.roleLabel.trim() || undefined,
      });
      logger.show_success("Teacher linked to section.");
      teacherAssignForm.reset({ userId: "", roleLabel: "" });
      setTeacherFormOpen(false);
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not assign teacher.",
      );
    }
  });

  const saveEditClassTeacher = editTeacherForm.handleSubmit(async (vals) => {
    if (!classId || !editTeacherRow) return;
    try {
      await updateClassSubject.mutateAsync({
        classId,
        subjectId: editTeacherRow.subject.subjectId,
        request: {
          subjectTeacherId: vals.subjectTeacherId.trim()
            ? vals.subjectTeacherId.trim()
            : null,
        },
      });
      logger.show_success("Subject teacher updated.");
      setEditTeacherRow(null);
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not update teacher.",
      );
    }
  });

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Box p={2} data-testid="academics-class-hub-no-institute">
          <Alert
            severity="info"
            data-testid="academics-class-hub-no-institute-alert"
          >
            Select an institute to manage classes.
          </Alert>
        </Box>
      </>
    );
  }

  if (!classId) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Alert
          severity="warning"
          data-testid="academics-class-hub-missing-class-id"
        >
          Missing class id.
        </Alert>
      </>
    );
  }

  if (classLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        py={6}
        data-testid="academics-class-hub-loading"
      >
        <CircularProgress data-testid="academics-class-hub-loading-spinner" />
      </Box>
    );
  }

  if (classError || !classRow) {
    return (
      <Box data-testid="academics-class-hub-error">
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Alert severity="error" data-testid="academics-class-hub-error-alert">
          Could not load this class. It may have been removed.
        </Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`${pages.academics.path}/classes`)}
          data-testid="academics-class-hub-back-to-classes"
        >
          Back to classes
        </Button>
      </Box>
    );
  }

  const title = `${classRow.name} · grade ${classRow.grade}`;

  return (
    <>
      <DocumentTitle title={`${CONSTANTS.title.academics} — ${title}`} />

      <Grid container spacing={3} data-testid="academics-class-hub">
        <Grid item xs={12} data-testid="academics-class-hub-header">
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ mb: 1 }}
            data-testid="academics-class-hub-breadcrumbs"
          >
            <Link
              component={RouterLink}
              to={`${pages.academics.path}/classes`}
              underline="hover"
              color="inherit"
              data-testid="academics-class-hub-breadcrumb-classes"
            >
              Classes
            </Link>
            <Typography color="text.primary">{classRow.name}</Typography>
            {sectionId ? (
              <Typography color="text.primary">Section teachers</Typography>
            ) : null}
          </Breadcrumbs>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <IconButton
              aria-label="Back"
              onClick={() =>
                sectionId
                  ? navigate(hubBase)
                  : navigate(`${pages.academics.path}/classes`)
              }
              size="small"
              data-testid="academics-class-hub-back-button"
            >
              <ArrowBackIcon />
            </IconButton>
            <MainTitle title={title} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Course: {course?.name ?? classRow.courseId} · Academic year{" "}
            {classRow.academicYear}
          </Typography>

          <Tabs
            value={isFeeStructureRoute ? 1 : 0}
            sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
            aria-label="Class hub sections"
            data-testid="academics-class-hub-tabs"
          >
            <Tab
              label="Sections & subjects"
              component={RouterLink}
              to={sectionsTabPath}
              data-testid="academics-class-hub-tab-sections"
            />
            <Tab
              label="Fee structure"
              component={RouterLink}
              to={feeStructurePath}
              data-testid="academics-class-hub-tab-fee-structure"
            />
          </Tabs>

          {isFeeStructureRoute ? (
            <Box sx={{ mt: 2 }} data-testid="academics-class-hub-fee-structure">
              <ClassFeeStructurePanel
                classId={classId}
                academicYear={classRow.academicYear}
              />
            </Box>
          ) : null}

          {!isFeeStructureRoute ? (
            <Card sx={{ mt: 2 }} data-testid="class-hub-hierarchy-jump-card">
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Jump to class / section
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use the dropdowns below to switch to another class or section.
                  You'll open that class hub; teachers and other details show when
                  they've been added for that section.
                </Typography>
                <AcademicHierarchySectionPicker
                  defaultSectionId={sectionId ?? null}
                  onSectionChange={(sid, ctx) => {
                    if (!sid || !ctx?.classId) return;
                    if (ctx.classId === classId && sid === sectionId) return;
                    navigate(
                      `${pages.academics.path}/classes/${ctx.classId}/sections/${sid}`,
                    );
                  }}
                />
              </CardContent>
            </Card>
          ) : null}
        </Grid>

        {!isFeeStructureRoute ? (
          <Box
            component="div"
            data-testid="academics-class-hub-main-content"
            sx={{ display: "contents" }}
          >
            <Grid
              item
              xs={12}
              md={sectionId ? 12 : 6}
              data-testid="class-hub-sections-column"
            >
              <Card data-testid="class-hub-sections-card">
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6">Sections</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setEditingSection(null);
                        setSectionDialogOpen(true);
                      }}
                      data-testid="add-section-button"
                    >
                      Add section
                    </Button>
                  </Box>
                  {sectionsLoading ? (
                    <CircularProgress
                      size={28}
                      data-testid="class-hub-sections-loading"
                    />
                  ) : (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      data-testid="class-hub-sections-table"
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(sectionList ?? []).map((sec) => (
                            <TableRow
                              key={sec.id}
                              data-testid={`class-hub-section-row-${sec.id}`}
                            >
                              <TableCell>{sec.name}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  startIcon={<PersonIcon />}
                                  onClick={() => openTeachersForSection(sec.id)}
                                  variant={
                                    sectionId === sec.id ? "contained" : "text"
                                  }
                                  data-testid={`section-teachers-${sec.id}`}
                                >
                                  Teachers
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingSection(sec);
                                    setSectionDialogOpen(true);
                                  }}
                                  aria-label="Edit section"
                                  data-testid={`edit-section-${sec.id}`}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteSection(sec)}
                                  aria-label="Delete section"
                                  data-testid={`delete-section-${sec.id}`}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {(sectionList ?? []).length === 0 && !sectionsLoading ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                      data-testid="class-hub-sections-empty"
                    >
                      No sections yet. Add one or create a new class (default section
                      &quot;A&quot; is added by the backend when supported).
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>

            <Grid
              item
              xs={12}
              md={sectionId ? 12 : 6}
              data-testid="class-hub-subjects-column"
            >
              <Card data-testid="class-hub-subjects-card">
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6">Class subjects</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      disabled={availableSubjects.length === 0}
                      onClick={() => setAssignSubjectOpen(true)}
                      data-testid="assign-class-subject-button"
                    >
                      Assign subject
                    </Button>
                  </Box>
                  {csLoading ? (
                    <CircularProgress
                      size={28}
                      data-testid="class-hub-subjects-loading"
                    />
                  ) : (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      data-testid="class-hub-subjects-table"
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Subject</TableCell>
                            <TableCell>Teacher id</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(classSubjects ?? []).map((row) => (
                            <TableRow
                              key={row.subjectId}
                              data-testid={`class-hub-subject-row-${row.subjectId}`}
                            >
                              <TableCell>
                                {row.subjectName ?? row.subjectId}{" "}
                                {row.subjectCode ? (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({row.subjectCode})
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell>{[row.subjectTeacherFirstName, row.subjectTeacherLastName]
                                .filter(Boolean)
                                .join(" ") ?? "—"}</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditTeacherRow({ subject: row })}
                                  aria-label="Edit teacher assignment"
                                  data-testid={`edit-class-subject-teacher-${row.subjectId}`}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={async () => {
                                    if (
                                      !window.confirm(
                                        "Remove this subject from the class?",
                                      )
                                    )
                                      return;
                                    try {
                                      await unassignSubject.mutateAsync({
                                        classId,
                                        subjectId: row.subjectId,
                                      });
                                      logger.show_success("Subject removed from class.");
                                    } catch (e) {
                                      logger.show_error(
                                        e instanceof Error
                                          ? e.message
                                          : "Remove failed.",
                                      );
                                    }
                                  }}
                                  aria-label="Remove subject from class"
                                  data-testid={`unassign-class-subject-${row.subjectId}`}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {sectionId ? (
              <Grid item xs={12} data-testid="class-hub-section-teachers-column">
                <Card data-testid="class-hub-section-teachers-card">
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="h6">Section teachers</Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setTeacherFormOpen(true)}
                        data-testid="assign-section-teacher-button"
                      >
                        Assign teacher
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Teachers linked to this section for scheduling and reporting.
                      Enter user IDs from your identity directory.
                    </Typography>
                    {teachersLoading ? (
                      <CircularProgress
                        size={28}
                        data-testid="class-hub-section-teachers-loading"
                      />
                    ) : (
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        data-testid="class-hub-section-teachers-table"
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>User id</TableCell>
                              <TableCell>Role</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(sectionTeachers ?? []).map((t) => (
                              <TableRow
                                key={t.userId}
                                data-testid={`class-hub-section-teacher-row-${t.userId}`}
                              >
                                <TableCell>
                                  {[t.firstName, t.lastName]
                                    .filter(Boolean)
                                    .join(" ") || "—"}
                                </TableCell>
                                <TableCell>{t.userId}</TableCell>
                                <TableCell>
                                  {t.role}
                                  {t.label ? ` · ${t.label}` : ""}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={async () => {
                                      try {
                                        await unassignTeacher.mutateAsync({
                                          sectionId,
                                          userId: t.userId,
                                        });
                                        logger.show_success("Teacher removed.");
                                      } catch (e) {
                                        logger.show_error(
                                          e instanceof Error
                                            ? e.message
                                            : "Remove failed.",
                                        );
                                      }
                                    }}
                                    aria-label="Remove teacher from section"
                                    data-testid={`unassign-section-teacher-${t.userId}`}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12} data-testid="class-hub-section-teachers-hint">
                <Alert severity="info" data-testid="class-hub-select-section-info">
                  Open <strong>Teachers</strong> on a section to load{" "}
                  <code>GET /sections/:id/teachers</code> and manage section staff.
                </Alert>
              </Grid>
            )}
          </Box>
        ) : null}
      </Grid>

      <Dialog
        open={sectionDialogOpen}
        onClose={() => {
          setSectionDialogOpen(false);
          setEditingSection(null);
        }}
        maxWidth="xs"
        fullWidth
        data-testid="class-hub-section-dialog"
      >
        <DialogTitle data-testid="class-hub-section-dialog-title">
          {editingSection ? "Edit section" : "Add section"}
        </DialogTitle>
        <form onSubmit={saveSection}>
          <DialogContent data-testid="class-hub-section-dialog-content">
            <ControlledTextField
              control={sectionForm.control as never}
              name="name"
              id="section-name"
              label="Name"
              required
              isGridItem={false}
              fullWidth
              inputProps={{ "data-testid": "section-name-input" }}
            />
            <Box sx={{ mt: 2 }}>
              <ControlledTextField
                control={sectionForm.control as never}
                name="description"
                id="section-description"
                label="Description"
                isGridItem={false}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ "data-testid": "section-description-input" }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSectionDialogOpen(false);
                setEditingSection(null);
              }}
              data-testid="class-hub-section-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              data-testid="class-hub-section-dialog-save"
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={assignSubjectOpen}
        onClose={() => setAssignSubjectOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="class-hub-assign-subject-dialog"
      >
        <DialogTitle data-testid="class-hub-assign-subject-dialog-title">
          Assign subject to class
        </DialogTitle>
        <form onSubmit={saveAssignSubject}>
          <DialogContent data-testid="class-hub-assign-subject-dialog-content">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a subject from the institute catalog. This links the
              subject to this class only.
            </Typography>
            {availableSubjects.length === 0 ? (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                data-testid="class-hub-assign-subject-empty-alert"
              >
                All catalog subjects are already assigned. Add more under
                Subjects or remove one from this class.
              </Alert>
            ) : (
              <Box data-testid="assign-subject-select">
                <ControlledSelectField
                  control={assignForm.control as never}
                  name="subjectId"
                  id="assign-subject-select"
                  label="Subject"
                  isRequired
                  isGridItem={false}
                  itemList={availableSubjects.map((s) => ({
                    key: s.id,
                    value: `${s.name} (${s.code})`,
                  }))}
                />
              </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                data-testid="class-hub-assign-subject-teacher-autocomplete"
                options={internalUsers}
                getOptionLabel={(u) =>
                  `${u.firstName} ${u.lastName}${u.email ? ` (${u.email})` : ""} — ${u.userRole}`
                }
                onChange={(_e, val) =>
                  assignForm.setValue("subjectTeacherId", val?.id ?? "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subject teacher (optional)"
                    size="small"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      "data-testid": "class-hub-assign-subject-teacher-input",
                    }}
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAssignSubjectOpen(false)}
              data-testid="class-hub-assign-subject-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              data-testid="class-hub-assign-subject-dialog-submit"
            >
              Assign
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={teacherFormOpen}
        onClose={() => setTeacherFormOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="class-hub-assign-teacher-dialog"
      >
        <DialogTitle data-testid="class-hub-assign-teacher-dialog-title">
          Assign teacher to section
        </DialogTitle>
        <form onSubmit={saveTeacherAssign}>
          <DialogContent data-testid="class-hub-assign-teacher-dialog-content">
            <ControlledTextField
              control={teacherAssignForm.control as never}
              name="userId"
              id="section-teacher-user"
              label="User id"
              required
              isGridItem={false}
              fullWidth
              inputProps={{ "data-testid": "section-teacher-user-input" }}
            />
            <Box sx={{ mt: 2 }}>
              <ControlledTextField
                control={teacherAssignForm.control as never}
                name="roleLabel"
                id="section-teacher-role"
                label="Role label (optional)"
                isGridItem={false}
                fullWidth
                inputProps={{ "data-testid": "section-teacher-role-input" }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setTeacherFormOpen(false)}
              data-testid="class-hub-assign-teacher-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              data-testid="class-hub-assign-teacher-dialog-submit"
            >
              Assign
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={!!editTeacherRow}
        onClose={() => setEditTeacherRow(null)}
        maxWidth="xs"
        fullWidth
        data-testid="class-hub-edit-subject-teacher-dialog"
      >
        <DialogTitle data-testid="class-hub-edit-subject-teacher-dialog-title">
          Assign subject teacher
        </DialogTitle>
        <form onSubmit={saveEditClassTeacher}>
          <DialogContent data-testid="class-hub-edit-subject-teacher-dialog-content">
            <Autocomplete
              data-testid="class-hub-edit-subject-teacher-autocomplete"
              options={internalUsers}
              getOptionLabel={(u) =>
                `${u.firstName} ${u.lastName}${u.email ? ` (${u.email})` : ""} — ${u.userRole}`
              }
              defaultValue={
                internalUsers.find(
                  (u) => u.id === editTeacherRow?.subject.subjectTeacherId,
                ) ?? null
              }
              onChange={(_e, val) =>
                editTeacherForm.setValue("subjectTeacherId", val?.id ?? "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Teacher"
                  size="small"
                  fullWidth
                  helperText="Select a teacher, internal staff, or admin user."
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": "class-hub-edit-subject-teacher-input",
                  }}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditTeacherRow(null)}
              data-testid="class-hub-edit-subject-teacher-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              data-testid="class-hub-edit-subject-teacher-dialog-save"
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default AcademicsClassHubView;
